import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Package,
  Plus,
  Search,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Truck,
  FileText,
  ChevronRight,
  ChevronDown,
  Edit2,
  Trash2,
  X,
  Phone,
  Mail,
  Building2,
  ArrowLeft,
  LogIn,
  LogOut,
  History,
  DollarSign,
  Send,
  RefreshCw,
  LayoutGrid,
  List,
  GripVertical
} from "lucide-react";
import BackorderPipeline from "@/components/BackorderPipeline";
import HospitalBackordersBoard from "@/components/hospital/HospitalBackordersBoard";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configurations
const STATUS_CONFIG = {
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700", icon: FileText },
  sent_to_suppliers: { label: "Sent to Suppliers", color: "bg-purple-100 text-purple-700", icon: Send },
  quotes_received: { label: "Quotes Received", color: "bg-amber-100 text-amber-700", icon: DollarSign },
  priced: { label: "Priced", color: "bg-cyan-100 text-cyan-700", icon: DollarSign },
  pending_approval: { label: "Pending Approval", color: "bg-orange-100 text-orange-700", icon: Clock },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  ordered: { label: "Ordered", color: "bg-indigo-100 text-indigo-700", icon: Package },
  shipped: { label: "Shipped", color: "bg-teal-100 text-teal-700", icon: Truck },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700", icon: X }
};

const ITEM_STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700" },
  quoted: { label: "Quoted", color: "bg-blue-100 text-blue-700" },
  need_more: { label: "Need More", color: "bg-amber-100 text-amber-700" },
  no_longer_needed: { label: "No Longer Needed", color: "bg-red-100 text-red-700" },
  ordered: { label: "Ordered", color: "bg-indigo-100 text-indigo-700" },
  shipped: { label: "Shipped", color: "bg-teal-100 text-teal-700" },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-700" }
};

// Registration Modal Component
const RegistrationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    hospital_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    reason: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hospitals/register/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Registration failed");
      }
      
      setSubmitted(true);
      toast.success("Registration request submitted!");
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      hospital_name: "",
      contact_name: "",
      email: "",
      phone: "",
      address: "",
      reason: ""
    });
    setSubmitted(false);
    onClose();
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Request Submitted!</h2>
            <p className="text-slate-500 mb-6">
              Your registration request has been submitted for review. You&apos;ll receive an email with your access code once approved.
            </p>
            <Button onClick={handleClose} className="w-full">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">Request Portal Access</DialogTitle>
          <DialogDescription>
            Fill out this form to request access to the Hospital Backorder Portal. We&apos;ll review your request and send you an access code.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Hospital/Facility Name *</label>
              <Input
                value={formData.hospital_name}
                onChange={(e) => setFormData({ ...formData, hospital_name: e.target.value })}
                placeholder="Memorial Hospital"
                required
                data-testid="register-hospital-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name *</label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="John Smith"
                required
                data-testid="register-contact-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(800) 988-3892"
                required
                data-testid="register-phone"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="purchasing@hospital.com"
                required
                data-testid="register-email"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Medical Center Dr, City, State ZIP"
                data-testid="register-address"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Why do you need access? (optional)</label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Tell us about your supply needs..."
                rows={3}
                data-testid="register-reason"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="register-submit">
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Login Form Component
const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/backorders/hospital/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, access_code: accessCode })
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Invalid email or access code");
      }
      
      const data = await res.json();
      localStorage.setItem("hospital_token", data.token);
      localStorage.setItem("hospital_info", JSON.stringify(data.hospital));
      onLogin(data.hospital);
      toast.success("Login successful!");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <LogIn className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Hospital Login</h2>
          <p className="text-slate-500 mt-2">Access your backorder requests</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@hospital.com"
              required
              data-testid="hospital-login-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Access Code</label>
            <Input
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
              placeholder="XXXXXXXX"
              maxLength={8}
              required
              className="tracking-widest text-center font-mono"
              data-testid="hospital-login-code"
            />
            <p className="text-xs text-slate-500 mt-1">8-character code provided by Sterile Mate</p>
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="hospital-login-submit">
            {loading ? "Logging in..." : "Login"}
          </Button>
        </form>
        
        <div className="mt-6 pt-6 border-t border-slate-200 text-center">
          <p className="text-sm text-slate-500 mb-3">Don&apos;t have an account?</p>
          <Button 
            variant="outline" 
            className="w-full mb-3"
            onClick={() => setShowRegister(true)}
            data-testid="request-access-btn"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Request Portal Access
          </Button>
          <p className="text-xs text-slate-500">
            Or submit a request below as a guest
          </p>
        </div>
      </div>
      
      <RegistrationModal isOpen={showRegister} onClose={() => setShowRegister(false)} />
    </>
  );
};

// Request Form Component
const RequestForm = ({ onSubmit, onCancel, prefillItem = null, prefillNotes = "" }) => {
  const [formData, setFormData] = useState({
    hospital_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    notes: prefillNotes,
    priority: "normal"
  });
  const [items, setItems] = useState([
    prefillItem
      ? { ...prefillItem }
      : { item_name: "", mfr_number: "", quantity: 1, quantity_type: "each", notes: "" }
  ]);
  const [loading, setLoading] = useState(false);

  const addItem = () => {
    setItems([...items, { item_name: "", mfr_number: "", quantity: 1, quantity_type: "each", notes: "" }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate items
    const validItems = items.filter(item => item.item_name.trim());
    if (validItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/backorders/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, items: validItems })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      
      toast.success("Request submitted successfully! Check your email for confirmation.");
      onSubmit(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
      <h2 className="text-2xl font-bold text-slate-800 mb-6">
        {prefillItem ? "Submit Quote Request" : "Submit Backorder Request"}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Contact Information */}
        <div className="bg-slate-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Contact Information</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hospital/Facility Name *</label>
              <Input
                value={formData.hospital_name}
                onChange={(e) => setFormData({ ...formData, hospital_name: e.target.value })}
                required
                data-testid="request-hospital-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name *</label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                required
                data-testid="request-contact-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                required
                data-testid="request-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
              <Input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                required
                data-testid="request-phone"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Shipping Address</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Street, City, State, ZIP"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="bg-slate-50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Items Needed</h3>
            <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Item
            </Button>
          </div>
          
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 grid md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Item Name *</label>
                      <Input
                        value={item.item_name}
                        onChange={(e) => updateItem(index, "item_name", e.target.value)}
                        placeholder="Product name"
                        data-testid={`item-name-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">MFR/Part #</label>
                      <Input
                        value={item.mfr_number}
                        onChange={(e) => updateItem(index, "mfr_number", e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="flex gap-2">
                      <div className="w-20">
                        <label className="block text-xs font-medium text-slate-500 mb-1">Qty *</label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                          data-testid={`item-qty-${index}`}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-slate-500 mb-1">Unit</label>
                        <select
                          value={item.quantity_type}
                          onChange={(e) => updateItem(index, "quantity_type", e.target.value)}
                          className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                        >
                          <option value="each">Each</option>
                          <option value="case">Case</option>
                          <option value="box">Box</option>
                          <option value="pack">Pack</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Notes (optional)</label>
                  <Input
                    value={item.notes}
                    onChange={(e) => updateItem(index, "notes", e.target.value)}
                    placeholder="Specific requirements, alternatives accepted, etc."
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Priority & Notes */}
        <div className="bg-slate-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Response Tier</h3>
          <p className="text-sm text-slate-500 mb-4">How urgently do you need this order fulfilled?</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {/* Standard Tier */}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, priority: "standard" })}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                formData.priority === "standard" || formData.priority === "normal" || formData.priority === "low"
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="tier-standard"
            >
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-slate-900">Standard</span>
              </div>
              <p className="text-2xl font-bold text-blue-600 mb-1">24-48 hrs</p>
              <p className="text-xs text-slate-500">Routine inventory requests</p>
            </button>

            {/* Priority Tier */}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, priority: "priority" })}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                formData.priority === "priority" || formData.priority === "high"
                  ? "border-amber-500 bg-amber-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="tier-priority"
            >
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-slate-900">Priority</span>
              </div>
              <p className="text-2xl font-bold text-amber-600 mb-1">4-8 hrs</p>
              <p className="text-xs text-slate-500">Urgent same-day needs</p>
            </button>

            {/* STAT Tier */}
            <button
              type="button"
              onClick={() => setFormData({ ...formData, priority: "stat" })}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                formData.priority === "stat" || formData.priority === "urgent"
                  ? "border-red-500 bg-red-50"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid="tier-stat"
            >
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-slate-900">STAT</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded-full font-medium">EMERGENCY</span>
              </div>
              <p className="text-2xl font-bold text-red-600 mb-1">2 hrs</p>
              <p className="text-xs text-slate-500">OR/ER surgery-critical</p>
            </button>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Additional Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any other details about your order..."
              rows={2}
            />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          )}
          <Button type="submit" disabled={loading} data-testid="submit-request-btn">
            {loading ? "Submitting..." : "Submit Request"}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Request List Component
const RequestList = ({ requests, onSelect, onRefresh, onDelete, token }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredRequests = requests.filter(req =>
    req.hospital_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (e, reqId) => {
    e.stopPropagation(); // Prevent triggering onSelect
    onDelete?.(reqId);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-800">Your Requests</h2>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search requests..."
          className="pl-10"
        />
      </div>

      {filteredRequests.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No requests found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRequests.map((req) => {
            const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.submitted;
            const StatusIcon = statusConfig.icon;
            const canDelete = req.status === "submitted" || req.status === "cancelled";
            
            return (
              <div
                key={req.id}
                onClick={() => onSelect(req)}
                className="border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                data-testid={`request-item-${req.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-sm text-slate-500">#{req.id.slice(0, 8).toUpperCase()}</p>
                    <p className="font-semibold text-slate-800 mt-1">
                      {req.items?.length || 0} item{req.items?.length !== 1 ? "s" : ""}
                    </p>
                    <p className="text-sm text-slate-500">
                      {new Date(req.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right flex items-start gap-2">
                    <div>
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                      {req.total_cost && (
                        <p className="mt-2 font-semibold text-green-600">${req.total_cost.toLocaleString()}</p>
                      )}
                    </div>
                    {canDelete && onDelete && (
                      <button
                        onClick={(e) => handleDelete(e, req.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                        title="Delete request"
                        data-testid={`delete-request-${req.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Request Detail Component
const RequestDetail = ({ request, token, onBack, onUpdate, onDelete }) => {
  const [editingItem, setEditingItem] = useState(null);
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({ item_name: "", mfr_number: "", quantity: 1, quantity_type: "each", notes: "" });
  const [loading, setLoading] = useState(false);

  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.submitted;
  const StatusIcon = statusConfig.icon;
  const canDelete = request.status === "submitted" || request.status === "cancelled";

  const handleUpdateItem = async (itemId, update) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/backorders/hospital/requests/${request.id}/items/${itemId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(update)
      });
      if (!res.ok) throw new Error("Update failed");
      toast.success("Item updated");
      onUpdate();
      setEditingItem(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Delete "${itemName}"? This cannot be undone.`)) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/backorders/hospital/requests/${request.id}/items/${itemId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Item deleted");
      onUpdate();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.item_name.trim()) {
      toast.error("Please enter an item name");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/backorders/hospital/requests/${request.id}/items`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(newItem)
      });
      if (!res.ok) throw new Error("Add failed");
      toast.success("Item added");
      onUpdate();
      setAddingItem(false);
      setNewItem({ item_name: "", mfr_number: "", quantity: 1, quantity_type: "each", notes: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </button>
        
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-slate-500">Request #{request.id.slice(0, 8).toUpperCase()}</p>
            <h2 className="text-2xl font-bold text-slate-800 mt-1">{request.hospital_name}</h2>
            <p className="text-slate-500">{request.contact_name} • {request.contact_email}</p>
          </div>
          <div className="text-right flex items-start gap-3">
            <div>
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${statusConfig.color}`}>
                <StatusIcon className="w-4 h-4" />
                {statusConfig.label}
              </span>
              {request.total_cost && (
                <p className="mt-3 text-2xl font-bold text-green-600">${request.total_cost.toLocaleString()}</p>
              )}
            </div>
            {canDelete && onDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(request.id)}
                className="text-red-600 hover:bg-red-50 border-red-200"
                data-testid="delete-request-detail-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Items ({request.items?.length || 0})</h3>
          <Button variant="outline" size="sm" onClick={() => setAddingItem(true)} data-testid="add-item-detail-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>

        <div className="space-y-3">
          {request.items?.map((item) => {
            const itemStatus = ITEM_STATUS_CONFIG[item.status] || ITEM_STATUS_CONFIG.pending;
            const isEditing = editingItem === item.id;

            return (
              <div key={item.id} className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-slate-800">{item.item_name}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${itemStatus.color}`}>
                        {itemStatus.label}
                      </span>
                    </div>
                    {item.mfr_number && (
                      <p className="text-sm text-slate-500 mt-1">MFR: {item.mfr_number}</p>
                    )}
                    <p className="text-sm text-slate-600 mt-1">
                      Quantity: <span className="font-medium">{item.quantity} {item.quantity_type}</span>
                    </p>
                    {item.notes && (
                      <p className="text-sm text-slate-500 mt-1 italic">{item.notes}</p>
                    )}
                    {item.final_price && (
                      <p className="text-sm font-semibold text-green-600 mt-2">
                        Unit Price: ${item.final_price.toFixed(2)} • Total: ${(item.final_price * item.quantity).toFixed(2)}
                      </p>
                    )}
                  </div>
                  
                  {item.status !== "no_longer_needed" && item.status !== "delivered" && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingItem(isEditing ? null : item.id)}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteItem(item.id, item.item_name)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`delete-item-${item.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Edit Options */}
                {isEditing && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateItem(item.id, { status: "need_more" })}
                        disabled={loading}
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        Need More
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateItem(item.id, { status: "no_longer_needed" })}
                        disabled={loading}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        No Longer Needed
                      </Button>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          defaultValue={item.quantity}
                          className="w-20 h-8"
                          id={`qty-${item.id}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => {
                            const newQty = parseInt(document.getElementById(`qty-${item.id}`).value);
                            if (newQty > 0) handleUpdateItem(item.id, { quantity: newQty });
                          }}
                          disabled={loading}
                        >
                          Update Qty
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Item Dialog */}
      <Dialog open={addingItem} onOpenChange={setAddingItem}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Item</DialogTitle>
            <DialogDescription>Add another item to this request</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Item Name *</label>
              <Input
                value={newItem.item_name}
                onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
                placeholder="Product name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">MFR/Part #</label>
              <Input
                value={newItem.mfr_number}
                onChange={(e) => setNewItem({ ...newItem, mfr_number: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Quantity</label>
                <Input
                  type="number"
                  min="1"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
                <select
                  value={newItem.quantity_type}
                  onChange={(e) => setNewItem({ ...newItem, quantity_type: e.target.value })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3"
                >
                  <option value="each">Each</option>
                  <option value="case">Case</option>
                  <option value="box">Box</option>
                  <option value="pack">Pack</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <Textarea
                value={newItem.notes}
                onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingItem(false)}>Cancel</Button>
            <Button onClick={handleAddItem} disabled={loading}>Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      {request.history && request.history.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <History className="w-5 h-5" /> History
          </h3>
          <div className="space-y-3">
            {request.history.slice().reverse().map((entry, idx) => (
              <div key={idx} className="flex items-start gap-3 text-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
                <div>
                  <p className="text-slate-800">{entry.details}</p>
                  <p className="text-slate-500 text-xs">
                    {new Date(entry.timestamp).toLocaleString()}
                    {entry.by && ` • ${entry.by}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Page Component
export default function HospitalBackorderPage() {
  const [hospital, setHospital] = useState(null);
  const [token, setToken] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [view, setView] = useState("form"); // form, list, detail, pipeline
  const [viewMode, setViewMode] = useState("list"); // list or pipeline
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Prefill from marketplace "Request Quote" links.
  // Marketplace generates URLs like:
  //   /hospital/backorders?prefill=1&item_name=X&mfr_number=Y&brand=Z&source=marketplace
  const prefillActive = searchParams.get("prefill") === "1";
  const prefillItemName = searchParams.get("item_name") || "";
  const prefillMfrNumber = searchParams.get("mfr_number") || "";
  const prefillBrand = searchParams.get("brand") || "";
  const prefillItem = prefillActive && prefillItemName
    ? {
        item_name: prefillItemName,
        mfr_number: prefillMfrNumber,
        quantity: 1,
        quantity_type: "each",
        notes: prefillBrand ? `Brand: ${prefillBrand}` : "",
      }
    : null;
  const prefillNotes = prefillActive
    ? `Quote requested from Sterile Mate Marketplace${prefillBrand ? ` — ${prefillBrand}` : ""}. Please add your facility name, contact info, and desired quantity.`
    : "";

  const fetchRequests = async (authToken) => {
    try {
      const res = await fetch(`${API_URL}/api/backorders/hospital/requests`, {
        headers: { "Authorization": `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests);
      }
    } catch (err) {
      console.error("Failed to fetch requests:", err);
    }
  };

  // Check for existing session - allow access if logged in, don't redirect
  useEffect(() => {
    const savedToken = localStorage.getItem("hospital_token");
    const savedUser = localStorage.getItem("hospital_user");
    const savedInfo = localStorage.getItem("hospital_info");
    
    // When arriving from marketplace with prefill params, keep the form open
    // even if the hospital is already authenticated.
    const shouldForceForm = prefillActive && prefillItemName;
    
    // If using the main hospital portal auth
    if (savedToken && savedUser) {
      setToken(savedToken);
      try {
        const userData = JSON.parse(savedUser);
        const hsData = localStorage.getItem("hospital_health_system");
        setHospital({
          name: hsData ? JSON.parse(hsData)?.name : userData.name,
          id: userData.health_system_id
        });
        fetchRequests(savedToken);
        setView(shouldForceForm ? "form" : "list");
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
    // Legacy hospital_info auth
    else if (savedToken && savedInfo) {
      setToken(savedToken);
      try {
        setHospital(JSON.parse(savedInfo));
        fetchRequests(savedToken);
        setView(shouldForceForm ? "form" : "list");
      } catch (e) {
        console.error("Error parsing hospital info:", e);
      }
    }
  }, []);

  const handleLogin = (hospitalInfo) => {
    // Redirect to hospital dashboard
    navigate("/hospital/dashboard");
  };

  const handleLogout = () => {
    localStorage.removeItem("hospital_token");
    localStorage.removeItem("hospital_info");
    setHospital(null);
    setToken(null);
    setRequests([]);
    setView("form");
    toast.success("Logged out");
  };

  const handleRequestSubmitted = (data) => {
    if (hospital) {
      fetchRequests(token);
      setView("list");
    } else {
      setView("form");
    }
  };

  // Delete a backorder request
  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm("Are you sure you want to delete this request? This action cannot be undone.")) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/backorders/hospital/requests/${requestId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Request deleted successfully");
        fetchRequests(token);
        if (selectedRequest?.id === requestId) {
          setSelectedRequest(null);
          setView("list");
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to delete request");
      }
    } catch (err) {
      toast.error("Failed to delete request");
    }
  };

  const handleSelectRequest = async (req) => {
    // Fetch full details
    try {
      const res = await fetch(`${API_URL}/api/backorders/hospital/requests/${req.id}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedRequest(data.request);
        setView("detail");
      }
    } catch (err) {
      toast.error("Failed to load request details");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-blue-600">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Home</span>
            </Link>
            
            <div className="flex items-center gap-2">
              <Package className="w-6 h-6 text-blue-600" />
              <span className="font-bold text-slate-800">Backorder Portal</span>
            </div>

            {hospital ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600 hidden sm:inline">{hospital.hospital_name}</span>
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-1" /> Logout
                </Button>
              </div>
            ) : (
              <div className="w-20" />
            )}
          </div>
        </div>
      </header>

      {/* Navigation tabs for logged in users */}
      {hospital && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-4">
                <button
                  onClick={() => { setView("list"); setSelectedRequest(null); }}
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    view === "list" || view === "detail"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="tab-my-requests"
                >
                  My Requests
                </button>
                <button
                  onClick={() => setView("form")}
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                    view === "form"
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid="tab-new-request"
                >
                  New Request
                </button>
              </div>
              
              {/* View Mode Toggle - Only show when in list view */}
              {(view === "list" || view === "detail") && (
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === "list" 
                        ? "bg-white shadow-sm text-blue-600" 
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    title="Table View"
                    data-testid="view-mode-list"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("pipeline")}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === "pipeline" 
                        ? "bg-white shadow-sm text-blue-600" 
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    title="Pipeline View"
                    data-testid="view-mode-pipeline"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero for guest */}
        {!hospital && view === "form" && (
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 rounded-2xl p-8 md:p-12 text-white mb-8">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                {prefillActive ? "Marketplace Quote Request" : "Hospital Backorder Portal"}
              </h1>
              <p className="text-blue-100 text-lg mb-6">
                {prefillActive
                  ? "You're one step from your quote. Add your facility details, adjust the quantity, and our team will follow up with your custom price + lead-time in one business day."
                  : "Can't find the supplies you need? Submit a backorder request and we'll source quotes from our network of suppliers. Get competitive pricing with fast turnaround."}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-blue-200">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>{prefillActive ? "Item pre-filled from marketplace" : "Multiple supplier quotes"}</span>
                </div>
                <div className="flex items-center gap-2 text-blue-200">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Competitive pricing</span>
                </div>
                <div className="flex items-center gap-2 text-blue-200">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Track your requests</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content based on view */}
        {!hospital && view === "form" && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <RequestForm
                onSubmit={handleRequestSubmitted}
                prefillItem={prefillItem}
                prefillNotes={prefillNotes}
              />
            </div>
            <div>
              <LoginForm onLogin={handleLogin} />
            </div>
          </div>
        )}

        {hospital && view === "form" && (
          <RequestForm
            onSubmit={handleRequestSubmitted}
            onCancel={() => setView("list")}
            prefillItem={prefillItem}
            prefillNotes={prefillNotes}
          />
        )}

        {hospital && view === "list" && (viewMode === "list" || viewMode === "board") && (
          <HospitalBackordersBoard user={hospital} token={token} />
        )}

        {hospital && view === "list" && viewMode === "table_legacy" && (
          <RequestList
            requests={requests}
            onSelect={handleSelectRequest}
            onRefresh={() => fetchRequests(token)}
            onDelete={handleDeleteRequest}
            token={token}
          />
        )}

        {hospital && view === "list" && viewMode === "pipeline" && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <BackorderPipeline 
              viewMode="hospital" 
              hospitalId={hospital?.id}
              authToken={token}
              onRefresh={() => fetchRequests(token)}
            />
          </div>
        )}

        {hospital && view === "detail" && selectedRequest && (
          <RequestDetail
            request={selectedRequest}
            token={token}
            onBack={() => { setView("list"); setSelectedRequest(null); }}
            onUpdate={() => handleSelectRequest(selectedRequest)}
            onDelete={handleDeleteRequest}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-400" />
              <div>
                <p className="font-bold">AskIsaac Inc.</p>
                <p className="text-sm text-slate-400">Healthcare Supply Solutions</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="tel:800-988-3892" className="flex items-center gap-2 text-slate-400 hover:text-white">
                <Phone className="w-4 h-4" /> 800.988.3892
              </a>
              <a href="mailto:support@sterilemate.com" className="flex items-center gap-2 text-slate-400 hover:text-white">
                <Mail className="w-4 h-4" /> support@sterilemate.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
